/* eslint-disable react/jsx-no-constructed-context-values */
import React, { useState, useEffect, createContext, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_ALL_ROLES } from 'pages/Login/LoginGraphQL';
import { redirectionURLs } from 'config/role';

export const RoleConfigContext = createContext({});

// ✅ Ye aapki API token hogi - environment variable se lena better hai
const API_TOKEN =
  process.env.REACT_APP_API_TOKEN ||
  'eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiI5MTMxNTE4MDExIiwiZXhwIjoxNzY5MTYyMjg5LCJpYXQiOjE3NjY1NzAyODksImlkIjo1ODI3LCJmaXJzdE5hbWUiOiJQZXJtaXNzaW9uIiwibWlkZGxlTmFtZSI6IiIsImxhc3ROYW1lIjoiVGVzdCIsImVtYWlsIjoiZG9zYXZpMzcxOUByb3JhdHUuY29tIiwicGhvbmVOdW1iZXIiOiI5MTMxNTE4MDExIiwiaXNEZWxldGVkIjpmYWxzZSwia3ljU3RhdHVzIjoiS1lDX1BFTkRJTkciLCJDQUxMSU5HIENPREUiOjEsIkNBTExJTkcgQ09ERSBJRCI6Iis5MyIsInJvbGVzIjpbIk1vaW4gU2hlaWtoIl0sImdzdFZlcmlmaWVkIjpmYWxzZSwiSVNfUkVHSVNURVJFRCI6ZmFsc2V9.lkmu5p4KwXo_E7ThE0hvMAUvXgFSg3VU46ITue0ycmI';

const RoleConfigProvider = ({ children }) => {
  const [roleConfig, setRoleConfig] = useState(redirectionURLs);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [fetchRoles] = useLazyQuery(GET_ALL_ROLES, {
    fetchPolicy: 'network-only',
  });

  // Load roles from API using system token
  const loadRolesFromAPI = useCallback(async () => {
    try {
      console.log('🔄 Fetching all roles from API...');

      const { data, error } = await fetchRoles({
        context: {
          headers: {
            authorization: `Bearer ${API_TOKEN}`,
          },
        },
      });

      if (error) {
        console.error('❌ Error fetching roles:', error);
        console.warn('⚠️ Using hardcoded role configurations as fallback');
        setRolesLoaded(true);
        return;
      }

      if (data?.roles) {
        console.log('✅ Roles fetched from API:', data.roles.length, 'roles');

        // Merge hardcoded roles with dynamic roles
        const dynamicConfig = { ...redirectionURLs };

        data.roles.forEach((role) => {
          // If role not in hardcoded config, set default route
          if (!dynamicConfig[role.name]) {
            dynamicConfig[role.name] = '/dashboard';
            console.log(`🆕 New role added: "${role.name}" -> /dashboard`);
          }
        });

        setRoleConfig(dynamicConfig);
        console.log(
          '📋 Total role configs loaded:',
          Object.keys(dynamicConfig).length,
        );
        console.log('📋 Available roles:', Object.keys(dynamicConfig));
      }
    } catch (error) {
      console.error('❌ Exception fetching roles:', error);
      console.warn('⚠️ Using hardcoded role configurations as fallback');
    } finally {
      setRolesLoaded(true);
    }
  }, [fetchRoles]);

  // ✅ Component mount hote hi roles fetch karo (before login)
  useEffect(() => {
    console.log('🚀 RoleConfigProvider mounted - loading roles...');
    loadRolesFromAPI();
  }, [loadRolesFromAPI]);

  /**
   * Get redirect URL for a given role
   * @param {string} roleName - Name of the role
   * @returns {string} - Redirect URL (default: /dashboard)
   */
  const getRedirectURL = useCallback(
    (roleName) => {
      const url = roleConfig[roleName] || '/dashboard';
      console.log(`🔀 Getting redirect for role "${roleName}": ${url}`);
      return url;
    },
    [roleConfig],
  );

  /**
   * Check if a user has a specific role
   * @param {Array} userRoles - Array of user role objects
   * @param {string} requiredRole - Role name to check
   * @returns {boolean}
   */
  const hasRole = useCallback((userRoles, requiredRole) => {
    if (!userRoles || !Array.isArray(userRoles)) return false;
    return userRoles.some((role) => role.name === requiredRole);
  }, []);

  /**
   * Reload roles from API (can be called manually)
   */
  const reloadRoles = useCallback(() => {
    console.log('🔄 Manual reload requested...');
    setRolesLoaded(false);
    return loadRolesFromAPI();
  }, [loadRolesFromAPI]);

  return (
    <RoleConfigContext.Provider
      value={{
        getRedirectURL,
        roleConfig,
        rolesLoaded,
        hasRole,
        reloadRoles,
      }}
    >
      {children}
    </RoleConfigContext.Provider>
  );
};

export default RoleConfigProvider;
